import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FileText } from "lucide-react";

const LogReportDialog = React.memo(function LogReportDialog({
  open, onOpenChange, reportUser, reportDays, setReportDays,
  reportFormat, setReportFormat, onGenerate
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Generate Log Report
          </DialogTitle>
          <DialogDescription>
            Generate a log report for <strong>{reportUser?.full_name}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reportDays">Number of days</Label>
            <div className="flex gap-2">
              <Input
                id="reportDays"
                type="number"
                min="1"
                max="365"
                value={reportDays}
                onChange={(e) => setReportDays(e.target.value)}
                placeholder="Enter days"
                className="flex-1"
              />
              <Select value={reportDays} onValueChange={setReportDays}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Quick select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500">Enter a custom number or use quick select</p>
          </div>

          <div className="space-y-2">
            <Label>Report Format</Label>
            <div className="flex gap-4">
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                reportFormat === "column" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
              }`}>
                <input
                  type="radio"
                  name="reportFormat"
                  value="column"
                  checked={reportFormat === "column"}
                  onChange={(e) => setReportFormat(e.target.value)}
                  className="sr-only"
                />
                <span className={`text-sm font-medium ${reportFormat === "column" ? "text-blue-700" : "text-slate-700"}`}>
                  Column Format
                </span>
              </label>
              <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                reportFormat === "row" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"
              }`}>
                <input
                  type="radio"
                  name="reportFormat"
                  value="row"
                  checked={reportFormat === "row"}
                  onChange={(e) => setReportFormat(e.target.value)}
                  className="sr-only"
                />
                <span className={`text-sm font-medium ${reportFormat === "row" ? "text-blue-700" : "text-slate-700"}`}>
                  Row Format
                </span>
              </label>
            </div>
            <p className="text-xs text-slate-500">
              {reportFormat === "column" ? "Shows each field in a separate column" : "Shows each date with fields stacked vertically"}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onGenerate} disabled={!reportDays || parseInt(reportDays) < 1}>
            <FileText className="mr-2 h-4 w-4" />
            Generate Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default LogReportDialog;
